import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useEffect, useMemo, useState} from 'react';
import {useWatch} from 'react-hook-form';
import type {ProjectRequest, SelectOption} from 'src/api/ProactApiClient';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {
  useCreateProjectMutation,
  useGetProjectQuery,
  useUpdateProjectMutation,
} from 'src/redux/queries/project';
import {useGetRegionSelectOptionsQuery} from 'src/redux/queries/region';
import {ProactField} from 'src/app/forms/engine/ProactField';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {SidebarLinkProjectsAdmin} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  GetDefaultProjectFormData,
  MapProjectFormRegionId,
  MapProjectResponseToFormData,
  ProjectGeneralFormMeta,
  PROJECT_LOCATION_FORM_META,
  PROJECT_OSHA_FORM_META,
} from './projectHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: 'Project',
  entityNamePlural: 'Projects',
  listPath: PathUtility.Project('list'),
  viewPath: id => PathUtility.ProjectView(id),
};

export interface ProjectFormProps {
  id?: number;
  mode: 'create' | 'view' | 'edit';
  // When embedded in a tabbed page the parent owns the BackBar + PageHeader.
  embedded?: boolean;
}

const applyReadOnly = (fields: IFormFieldV2[]): IFormFieldV2[] =>
  fields.map(f => ({...f, readOnly: true}));

export const ProjectForm: React.FC<ProjectFormProps> = ({
  id,
  mode,
  embedded = false,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();
  const [isSavePending, setIsSavePending] = useState(false);

  const perms = useModulePermissions(ModuleEnum.Projects);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  const [createProject] = useCreateProjectMutation();
  const [updateProject] = useUpdateProjectMutation();

  const {
    data: projectData,
    isLoading,
    error: fetchError,
  } = useGetProjectQuery(id!, {skip: id === undefined});

  const {data: regions} = useGetRegionSelectOptionsQuery(
    {start: 0, length: 10000, filters: [], filterFns: [], sorting: []},
    {skip: isViewMode}
  );

  // View mode resolves the label from the project's own regionName instead of
  // fetching every option, since the select is never opened.
  const regionOptions = useMemo<SelectOption[]>(() => {
    if (!isViewMode) return regions?.data ?? [];
    return projectData?.regionId
      ? [{id: projectData.regionId, label: projectData.regionName ?? ''}]
      : [];
  }, [isViewMode, regions, projectData]);

  const generalFormMeta = useMemo<IFormFieldV2[]>(() => {
    const meta = ProjectGeneralFormMeta(regionOptions);
    return isViewMode ? applyReadOnly(meta) : meta;
  }, [isViewMode, regionOptions]);

  const locationFormMeta = useMemo<IFormFieldV2[]>(
    () =>
      isViewMode
        ? applyReadOnly(PROJECT_LOCATION_FORM_META)
        : PROJECT_LOCATION_FORM_META,
    [isViewMode]
  );

  const oshaFormMeta = useMemo<IFormFieldV2[]>(
    () =>
      isViewMode ? applyReadOnly(PROJECT_OSHA_FORM_META) : PROJECT_OSHA_FORM_META,
    [isViewMode]
  );

  const allFormMeta = useMemo<IFormFieldV2[]>(
    () => [...generalFormMeta, ...locationFormMeta, ...oshaFormMeta],
    [generalFormMeta, locationFormMeta, oshaFormMeta]
  );

  const form = useEntitySyncedForm({
    formMeta: allFormMeta,
    initialData: GetDefaultProjectFormData(),
    entity: projectData,
    toFormData: MapProjectResponseToFormData,
  });

  useEffect(() => {
    if (fetchError) {
      setAlert(parseErrorMessage(fetchError), 'error');
    }
  }, [fetchError, setAlert]);

  const projectName = useWatch({control: form.methods.control, name: 'name'});
  const projectCode = useWatch({control: form.methods.control, name: 'code'});
  const projectActive = useWatch({
    control: form.methods.control,
    name: 'active',
  });

  const handleSave = async () => {
    try {
      setIsSavePending(true);

      const values = form.methods.getValues();
      const body: ProjectRequest = {
        id: id,
        name: values.name as string,
        code: values.code as string | undefined,
        description: values.description as string | undefined,
        address: values.address as string | undefined,
        city: values.city as string | undefined,
        state: values.state as string | undefined,
        country: values.country as string | undefined,
        postalCode: values.postalCode as string | undefined,
        timeZone: values.timeZone as string | undefined,
        active: values.active as boolean,
        regionId: MapProjectFormRegionId(values.regionId),
        oshaIndustryDescription: values.oshaIndustryDescription as
          | string
          | undefined,
        oshaSicCode: values.oshaSicCode as string | undefined,
        oshaNaicsCode: values.oshaNaicsCode as string | undefined,
      };

      const res =
        id === undefined
          ? await createProject(body).unwrap()
          : await updateProject(body).unwrap();

      setAlert(
        id === undefined
          ? `${values.name} was successfully created`
          : `${values.name} was successfully updated`,
        'success'
      );

      navigate(PathUtility.ProjectView(String(res.id)), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setIsSavePending(false);
    }
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id ? PathUtility.ProjectView(id) : PathUtility.Project('list')
  );

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode,
        id,
        itemName: projectName as string,
      }),
    [mode, id, projectName]
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const saveButtons = (
    <>
      <PageHeader.Action variant="cancel" onClick={handleCancel}>
        Cancel
      </PageHeader.Action>
      <PageHeader.Action
        variant={isCreateMode ? 'create' : 'save'}
        onClick={handleValidateAndSave}
        loading={isSavePending}>
        {isCreateMode ? 'Create' : 'Save Changes'}
      </PageHeader.Action>
    </>
  );

  const formContent = (
    <ProactForm form={form}>
      <Stack gap="md">
        <SectionCard title="General" data-testid="section-general">
          <Box p="md">
            <Grid gutter="md">
              <ProactField name="name" />
              <ProactField name="code" />
              <ProactField name="regionId" />
              <ProactField name="description" />
              <ProactField name="active" />
            </Grid>
          </Box>
        </SectionCard>

        <SectionCard title="Location" data-testid="section-location">
          <Box p="md">
            <Grid gutter="md">
              <ProactField name="address" />
              <ProactField name="city" />
              <ProactField name="state" />
              <ProactField name="country" />
              <ProactField name="postalCode" />
              <ProactField name="timeZone" />
            </Grid>
          </Box>
        </SectionCard>

        <SectionCard title="OSHA Establishment" data-testid="section-osha">
          <Box p="md">
            <Grid gutter="md">
              <ProactField name="oshaIndustryDescription" />
              <ProactField name="oshaSicCode" />
              <ProactField name="oshaNaicsCode" />
            </Grid>
          </Box>
        </SectionCard>

        {!isViewMode && <Group mt="md">{saveButtons}</Group>}
      </Stack>
    </ProactForm>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <Stack gap="lg">
      <BackBar
        fallbackTo={navigation.fallbackTo}
        fallbackLabel={navigation.fallbackLabel}
        breadcrumbs={navigation.breadcrumbs}
      />

      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkProjectsAdmin.icon}
          subtitle={isCreateMode ? undefined : (projectCode as string)}>
          {isCreateMode ? 'New Project' : (projectName as string) || 'Project'}
        </PageHeader.Title>

        {isViewMode && (
          <PageHeader.Badges>
            <PageHeader.Badge
              color={projectActive ? 'green' : 'gray'}
              variant="light">
              {projectActive ? 'Active' : 'Inactive'}
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          {isViewMode && perms.update && (
            <PageHeader.Action
              variant="edit"
              onClick={() => navigate(PathUtility.ProjectEdit(id!))}>
              Edit
            </PageHeader.Action>
          )}

          {!isViewMode && saveButtons}
        </PageHeader.Actions>
      </PageHeader>

      {formContent}
    </Stack>
  );
};
